import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

interface SectionNavigatorProps {
  sectionId: string;
  previousSectionId?: string;
  nextSectionId?: string;
  position?: "top" | "bottom" | "both";
  variant?: "floating" | "integrated";
}

const SectionNavigator: React.FC<SectionNavigatorProps> = ({
  sectionId,
  previousSectionId,
  nextSectionId,
  position = "both",
  variant = "integrated",
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const scrollToSection = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      // Calculate exact position for mobile to prevent overshooting
      const headerOffset = 80; // Adjust if needed based on any fixed headers
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      // Use scrollTo for more controlled scrolling behavior
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Standardized button class to match desktop exactly
  const navButtonClass = "flex items-center justify-center w-7 h-7 rounded-full bg-primary/50 hover:bg-primary/70 text-white shadow-md backdrop-blur-sm transition-all duration-300";

  const renderTopArrow = () => {
    if (!previousSectionId || (position !== "top" && position !== "both")) return null;
    
    if (variant === "integrated") {
      return (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
          <button
            onClick={() => scrollToSection(previousSectionId)}
            className={navButtonClass}
            aria-label="Navigate to previous section"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      );
    }
    
    return (
      <div className="w-full flex justify-center -mt-6 mb-4">
        <button
          onClick={() => scrollToSection(previousSectionId)}
          className={navButtonClass}
          aria-label="Navigate to previous section"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const renderBottomArrow = () => {
    if (!nextSectionId || (position !== "bottom" && position !== "both")) return null;
    
    if (variant === "integrated") {
      return (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
          <button
            onClick={() => scrollToSection(nextSectionId)}
            className={navButtonClass}
            aria-label="Navigate to next section"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      );
    }
    
    return (
      <div className="w-full flex justify-center mt-4 -mb-6">
        <button
          onClick={() => scrollToSection(nextSectionId)}
          className={navButtonClass}
          aria-label="Navigate to next section"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="relative md:hidden">
      {renderTopArrow()}
      {renderBottomArrow()}
    </div>
  );
};

export default SectionNavigator; 